import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../../utils/axios';
import { useBookingContext } from '../../context/BookingContext';
import { formatDateTime } from '../../utils/DateUtil';

const Booking = () => {
  const { state, dispatch } = useBookingContext();
  const { bookings, users, vehicles, filters, page, totalPages } = state;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const filterType = queryParams.get('type');


  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');

        // Determine filter overrides based on query
        let statusFilter = filters.status;

        /* if (filterType === 'passenger') statusFilter = 'PENDING_PASSENGERS';
        else if (filterType === 'approver') statusFilter = 'PENDING_APPROVER';
        else if (filterType === 'admin') statusFilter = 'PENDING_ADMIN'; */

        const extra = {};
        if (filterType === 'passenger') {
          statusFilter = 'PENDING_PASSENGERS';
          extra.pendingPassenger = '1'; // backend will use req.user._id
        } else if (filterType === 'approver') {
          statusFilter = 'PENDING_APPROVER';
          extra.pendingApprover = '1';
        } else if (filterType === 'admin') {
          statusFilter = 'PENDING_ADMIN';
          extra.pendingAdmin = '1';
        }
        
        const queryParams = new URLSearchParams({
          requester: filters.requester,
          status: statusFilter,
          office: filters.office,
          vehicle: filters.vehicle,
          page,
          ...extra,
        });

        const [bookingRes, userRes, vehicleRes] = await Promise.all([
          api.get(`/api/car-bookings?${queryParams.toString()}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          api.get('/api/users', { headers: { Authorization: `Bearer ${token}` } }),
          api.get('/api/vehicles', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        dispatch({
          type: 'SET_BOOKINGS',
          payload: {
            bookings: bookingRes.data.bookings,
            totalPages: bookingRes.data.totalPages
          }
        });
        dispatch({ type: 'SET_USERS', payload: userRes.data });
        dispatch({ type: 'SET_VEHICLES', payload: vehicleRes.data });
        dispatch({ type: 'SET_TOTAL_PAGES', payload: bookingRes.data.totalPages });
      } catch (err) {
        console.error('Failed to fetch data', err);
      }      
    };

    fetchData();
    
  }, [filters, page, dispatch, filterType]);

  const sortedUsers = [...users].sort((a, b) => a.name.localeCompare(b.name));
  const sortedVehicles = [...vehicles].sort((a, b) => {
    const keyA = `${a.office}-${a.name}-${a.plateNumber}`;
    const keyB = `${b.office}-${b.name}-${b.plateNumber}`;
    return keyA.localeCompare(keyB);
  });

  return (
    <div className="container-fluid">
      <h1 className="text-2xl font-semibold mb-4">Booking Dashboard</h1>

      <div className="mb-4">
        <Link to="/create-booking" className="btn btn-primary">+ New Booking</Link>
      </div>

      {filterType && (
        <div className="alert alert-danger">
          <strong>
            {filterType === 'passenger' && 'Please confirm the following booking(s)'}
            {filterType === 'approver' && 'Please approve or reject the following booking(s)'}
            {filterType === 'admin' && 'Please grant or reject the following booking(s)'}
          </strong>
        </div>
      )}


      {/* Filters */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <label className="form-label fw-bold">Requester</label>
          <select
            className="form-select"
            value={filters.requester}
            onChange={(e) =>
              dispatch({ type: 'SET_FILTERS', payload: { ...filters, requester: e.target.value } })
            }
          >
            <option value="">All</option>
            {sortedUsers.map((u) => (
              <option key={u._id} value={u._id}>{u.name}</option>
            ))}
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label fw-bold">Status</label>
          <select
            className="form-select"
            value={filters.status}
            onChange={(e) =>
              dispatch({ type: 'SET_FILTERS', payload: { ...filters, status: e.target.value } })
            }
          >
            <option value="">All</option>
            {["PENDING_PASSENGERS", "PENDING_APPROVER", "PENDING_ADMIN", "CONFIRMED", "REJECTED", "GRANTED"].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label fw-bold">Office</label>
          <select
            className="form-select"
            value={filters.office}
            onChange={(e) =>
              dispatch({ type: 'SET_FILTERS', payload: { ...filters, office: e.target.value } })
            }
          >
            <option value="">All</option>
            <option value="HQ">HQ</option>
            <option value="SITE">Site</option>
          </select>
        </div>

        <div className="col-md-3">
          <label className="form-label fw-bold">Vehicle</label>
          <select
            className="form-select"
            value={filters.vehicle}
            onChange={(e) =>
              dispatch({ type: 'SET_FILTERS', payload: { ...filters, vehicle: e.target.value } })
            }
          >
            <option value="">All</option>
            {sortedVehicles.map(v => (
              <option key={v._id} value={v._id}>
                {`${v.office} - ${v.name} - ${v.plateNumber}`}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table className="table table-striped table-bordered align-middle text-start">
          <thead className="table-light">
            <tr className="table-primary">
              <th>Requester</th>
              <th>Destination</th>
              <th>From</th>
              <th>To</th>
              <th>Passengers</th>
              <th>Approver</th>
              <th>Vehicle</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center">No bookings found.</td>
              </tr>
            ) : (
              bookings.map(booking => {
                const isPast = new Date() > new Date(booking.endDate);
              //  console.log(new Date());
                return (
                  <tr key={booking._id} className={isPast ? "table-success text-muted" : ""}>
                    <td>{booking.requester.name}</td>
                    <td>
                      <Link to={`/bookings/${booking._id}`} className="text-decoration-none text-primary">
                        {booking.destination}
                      </Link>
                    </td>
                    <td>{formatDateTime(booking.startDate)}</td>
                    <td>{formatDateTime(booking.endDate)}</td>
                    <td>
                      <small>
                        {booking.userPassengers.map((p, idx) => (
                          <div key={idx}>{p.confirmed ? '✅' : '❌'} {p.user?.name || 'Unknown'}</div>
                        ))}
                        {booking.nonUserPassengers?.filter(name => name.trim()).length > 0 && (
                          <>
                            <hr />
                            <span>Other Passengers:</span>
                            {booking.nonUserPassengers?.[0]
                              .split(',')
                              .map((name, idx) => (
                                <li key={idx}>{name.trim()}</li>
                              ))}
                          </>
                        )}
                      </small>
                    </td>
                    <td>{['PENDING_PASSENGERS', 'PENDING_APPROVER'].includes(booking.status) ? '❌' : '✅'} {booking.approver.name}</td>
                    <td>{booking.preferredVehicle?.name}</td>
                    <td data-bs-toggle="tooltip" data-bs-placement="bottom" data-bs-original-title="Tooltip on bottom">
                      <span className={
                        booking.status === 'REJECTED' ? 'badge bg-danger' :
                        booking.status === 'GRANTED' ? 'badge bg-success' :
                        booking.status === 'PENDING_APPROVER' ? 'badge bg-info' :
                        booking.status === 'PENDING_PASSENGERS' ? 'badge bg-warning' :
                        'badge bg-info'
                      }>
                        {booking.status}
                      </span>
                      {booking.status === 'GRANTED' && (
                        <div>(by: {booking.grantedBy?.name})</div>
                      )}
                      {booking.status === 'REJECTED' && (
                        <div>(by: {booking.rejectedBy?.name})</div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="d-flex justify-content-center mt-4 gap-3">
        <button
          className="btn btn-outline-primary"
          onClick={() => dispatch({ type: 'SET_PAGE', payload: page - 1 })}
          disabled={page === 1}
        >
          Previous
        </button>
        <span className="fw-bold">Page {page} of {totalPages}</span>
        <button
          className="btn btn-outline-primary"
          onClick={() => dispatch({ type: 'SET_PAGE', payload: page + 1 })}
          disabled={page === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Booking;
